import * as Sentry from "@sentry/react";
import debug from "debug";
import { genericPathname } from "src/util/url";

const disabledSentryLog = debug("orchestrated:sentry-disabled");

const isErrorReportingEnabled =
  process.env.REACT_APP_ENABLE_ERROR_REPORTING === "true";

// XHR/img/FileReader handlers reject with ProgressEvent/Event (not Error), which
// Sentry cannot attribute — see APP 7505789215 / sentry-javascript#2210.
export const isDomEventRejection = (exception) => {
  if (!exception) {
    return false;
  }
  if (exception instanceof Error) {
    return exception.cause ? isDomEventRejection(exception.cause) : false;
  }
  const eventType = exception?.type;
  const constructorName = exception?.constructor?.name;
  return (
    eventType === "error" &&
    (constructorName === "ProgressEvent" || constructorName === "Event")
  );
};

const makeBeforeSendHandler = () => {
  if (isErrorReportingEnabled) {
    return (event, hint) => {
      if (isDomEventRejection(hint?.originalException)) {
        return null;
      }

      const operationName =
        hint?.captureContext?.extra?.operation?.operationName;
      const statusCode = hint?.originalException?.statusCode;
      const ignoredOperation =
        operationName === "getActiveWorkspace" ||
        operationName === "getWorkspaces" ||
        operationName === "getContext" ||
        operationName === "getWorkspace";

      if (ignoredOperation && statusCode === 400) {
        return null;
      }

      return event;
    };
  }
  return (event, hint) => {
    disabledSentryLog("Event %o, hint %o", event, hint);
    // handler returns null - this stops event going to sentry
    return null;
  };
};

const getTracingConfig = () => {
  if (process.env.REACT_APP_SENTRY_ENABLE_TRACING === "true") {
    return {
      integrations: [
        Sentry.browserProfilingIntegration(),
        Sentry.browserTracingIntegration({
          beforeStartSpan: (startSpanOptions) => {
            return {
              ...startSpanOptions,
              name: genericPathname(location.pathname),
            };
          },
        }),
      ],
      options: {
        // Please do not change the sample rate from 10% without consulting Ryan first.
        tracesSampleRate: 0.1,
        profilesSampleRate: 0.1,
        tracePropagationTargets: [
          "localhost",
          "api.orchestrated.io",
          "api.dev.orchestrated.io",
          "api.integration.orchestrated.io",
          "api.dev.teamform.co",
          "api.teamform.co",
        ],
      },
    };
  }
  return { options: { tracesSampleRate: 0 }, integrations: [] };
};

export function getBeforeSendForTesting() {
  return makeBeforeSendHandler();
}

export function initErrorReporting() {
  const tracingConfig = getTracingConfig();
  Sentry.init({
    dsn: process.env.REACT_APP_SENTRY_DSN,
    // Valid error scenarios that do not require review
    ignoreErrors: [
      "WORKSPACE_NOT_FOUND",
      "Login required",
      "Readonly mode, can not run mutations",
      "Failed to fetch",
      "Saving zero FTE is not allowed",
      "Please fill in empty attribute label in attributes",
      // HELP-10499: CefSharp bot noise (Outlook SafeSearch, scanners). Not app code.
      "Object Not Found Matching Id:",
      // Apollo / fetch abort when navigation supersedes in-flight requests (not actionable).
      "AbortError: The operation was aborted.",
    ],
    normalizeDepth: 6,
    maxValueLength: 500,
    environment: process.env.REACT_APP_SENTRY_ENV,
    release: `${process.env.REACT_APP_SENTRY_ENV}_${process.env.REACT_APP_GIT_SHA}`,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1,
    beforeSend: makeBeforeSendHandler(),
    autoSessionTracking: isErrorReportingEnabled,
    sendClientReports: isErrorReportingEnabled,
    integrations: [
      ...tracingConfig.integrations,
      Sentry.httpClientIntegration(),
    ],
    ...tracingConfig.options,
  });
}
// TODO : Consider enhancing with these contexts as they are loaded rather than upfront
// - e.g. useWorkspace should set context for sentry etc.
export function configureErrorReporting(
  user,
  tenant,
  workspace,
  uiVersion,
  apiVersion
) {
  const scope = Sentry.getCurrentScope();
  scope.setUser({
    id: user.email,
    username: user.preferredUsername,
    email: user.email,
  });
  scope.setTag("tenant", tenant);
  scope.setExtra("tenant", tenant);
  scope.setExtra("workspace", workspace);
  scope.setExtra("ui_version", uiVersion);
  scope.setExtra("api_version", apiVersion);
}

export function reportError(error, errorInfo) {
  const scope = Sentry.getCurrentScope();

  const operationName = errorInfo?.operation?.operationName;

  const networkContext = {
    errorName: error?.name,
    networkErrorName: error?.networkError?.name,
    networkErrorMessage: error?.networkError?.message,
    hasNetworkResponse: !!error?.networkError?.response,
    navigatorOnLine:
      typeof navigator !== "undefined" ? navigator.onLine : undefined,
  };

  const extra = { ...errorInfo, ...networkContext };

  if (operationName) {
    const message = error?.message;
    scope.setFingerprint([`${operationName} - ${message}`]);
    const newError = new Error(`${operationName} - ${message}`);
    newError.stack = error?.stack;
    Sentry.captureException(newError, { extra });
  } else {
    Sentry.captureException(error, { extra });
  }
}

export function reportMessage(message, context) {
  Sentry.captureMessage(message, context);
}
